import { stripQuerystring } from 'util/urls';
import { determineApiPrefix, determineTestCaseId} from 'util/env';
import { getResponseTransformer } from 'service-transformers';
import { makeError, treatErrorAs } from 'api/errors';
import { rejectAfter } from 'util/promise';
import { errorLevel } from 'constants/errors';
import { HTTP, SIGNAL, SIGNAL_KEY, MAX_RETRY_ATTEMPTS, FETCH_KILL_SWITCH } from 'constants/fetch';
import { timeouts } from 'constants/values';


import { trackError } from 'util/error';


/* Notes on some unusual behaviors:
 * set useClimateAuthHeaders: false to avoid sending CORS pre-flight OPTIONS request, but you'll have no auth
 *
 * CORS errors on OPTIONS requests are likely if the service does not return 200 or 404
 */

/** If the URL was hacked to include a test case ID, and if the test case rules match the request, then transform
    the response by chaining a then handler. Otherwise, just return the original response promise. */
const _transformResponseIfApplicableTestCase = ({url, body, responsePromise}) => {
  const testCaseId = determineTestCaseId({});
  const transformer = testCaseId ? getResponseTransformer({testCaseId, url, body}) : null;
  return transformer
    ? responsePromise.then((responseData) => Promise.resolve(transformer(responseData)))
    : responsePromise;
};

const generateHeaderOptions = ({ nextToken, useClimateAuthHeaders, userId, extraHeaders }) => {
  let headerOptions = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...extraHeaders
  };

  // Conditional as hack to handle those services that fart on an OPTIONS request
  // No custom headers and only GET or POST means no OPTIONS call
  if ( useClimateAuthHeaders ) {
    headerOptions = {
      ...headerOptions,
      'x-user-id': userId,
      'x-http-caller-id': 'variable-rate'
    };
  }
  // Some services use a token in the header to page responses
  if ( nextToken ) {
    headerOptions['x-next-token'] = nextToken;
  }
  return headerOptions;
};

const generateFetchOptions = ({ headerOptions, method, body, redirect, useSameOrigin, timeout }) => {
  // According to Bob, credentials: 'same-origin' should be used to deal with s3 redirection problem
  // https://climate.jira.com/browse/SYNTH-1665
  const fetchOptions = {
    credentials: useSameOrigin ? 'same-origin' : 'include',
    headers: new Headers(headerOptions),
    redirect,
    timeout
  };
  if(method) {
    fetchOptions.method = method;
  }
  if(body) {
    fetchOptions.body = body;
  }

  return fetchOptions;
};

const makeErrorHandler = ({ toTryAgain, attemptCount, messageSource, fetchJson }) => {
  return ( error ) => {
    // All valid responses, including errors, go to the then() clause.  This will be network issues only.
    // OR it will be any errors on the OPTIONS preflight request :/
    // Cloudfront will occasionally do this, we'll just reissue the request
    if(attemptCount < MAX_RETRY_ATTEMPTS) {
      treatErrorAs({ severity: errorLevel.INFO, message: `${error} for ${messageSource}`})(error);
      return fetchJson({...toTryAgain, attemptCount: attemptCount+1});
    }
    throw error;
  };
};

const serviceDownHandler = ({ toTryAgain, attemptCount, messageSource, fetchJson, response }) => {
  // Cloudfront will occasionally do this, we'll just reissue the request
  if(attemptCount < MAX_RETRY_ATTEMPTS) {
    treatErrorAs({
      severity: errorLevel.INFO,
      message: `reattempting due to 503 for ${messageSource}`,
      andHandleNow: true
    })(response);
    return fetchJson({...toTryAgain, attemptCount: attemptCount+1});
  } else {
    throw response;
  }
};

const authFailHandler = ({ messageSource }) => {
  throw makeError({
    severity: errorLevel.REAUTHENTICATE,
    error: new Error(`401 from ${messageSource}`)
  });
};

const accumulatePages = ({ toTryAgain, collection=[], fetchJson, nextToken }) => {
  return fetchJson({ ...toTryAgain, nextToken })
  .then( result => {
    if( result[SIGNAL_KEY] === SIGNAL.ANOTHER_SERVICE_PAGE_AWAITS ) {
      collection.push(result.data);
      // Grab next page of data
      return accumulatePages({ toTryAgain, collection, fetchJson, nextToken: result.nextToken });
    }
    if( result[SIGNAL_KEY] === SIGNAL.EMPTY_SERVICE_PAGE ) {
      // a 304 means previous 206 fell right on limit division and this result set is empty
      return collection;
    }
    // This is the final, unwrapped result
    collection.push(result);
    return collection;
  });
};

const accumulateImageryPages = ({ toTryAgain, data = [], fetchJson, nextUrl }) => {
  return fetchJson({ ...toTryAgain, url: nextUrl ? nextUrl : toTryAgain.url })
  .then( result => {
    if( result.paging.next ) {
      data.push(...result.data);
      return accumulateImageryPages({ toTryAgain, data, fetchJson, nextUrl: result.paging.next });
    }
    data.push(...result.data);
    return { data };
  });
};

const nextPageHandler = ({ response, responseJson }) => {
  const nextToken = response.headers.get('x-next-token');
  return (responseJson || response.json())
  .then( data => ({
    [SIGNAL_KEY]: SIGNAL.ANOTHER_SERVICE_PAGE_AWAITS,
    nextToken,
    data
  }) );
};

const finalPageHandler = () => {
  return Promise.resolve()
  .then( () => ({
    [SIGNAL_KEY]: SIGNAL.EMPTY_SERVICE_PAGE
  }) );
};

/** Fetch from a URL, with logic added to handle typical TCC service calling conventions and use cases.
 *
 * @param attemptCount (optional) # of times to attempt a fetch in response to a 503 HTTP status or network-level/CORS
 *           failure (no HTTP status). 1 (default) means try one time (no reattempts)
 * @param body  (Optional) string containing body to send in HTTP request.
 * @param defaultResponse (Optional) A string response body that can be returned for non-throwing errors. (See @throws)
 * @param isPagedData (optional) Boolean to return array of call results for x-next-token paged data.  Default: false
 * @param method (optional) string containing HTTP request method, e.g. POST, PUT, GET, DELETE
 * @param modifyResponse (optional) Function to modify response in case it needs to be handled differently per service.
 * @param nextToken (optional) string containing the x-next-token header used for paged requests in some services
 * @param redirect (optional) How redirect response will be handled. Either "follow" (default) or "manual".
 * @param redirectsToLoginOnAuthFail (optional) If true (default), an HTTP 401 status navigates to login screen.
 * @param requiresOldApiDomain (optional) Default is false. If true, will use a legacy domain prefix in the URL.
 * @param timeout (optional) Milliseconds to wait before canceling a request. Defaults to service call timeout constant.
 * @param url URL to fetch against which may include querystring params. To override the domain prefixing, you can
 *            include HTTP scheme and domain at beginning of URL, e.g. "https://some-completely-different-domain/" but
 *            you should have a good reason to do that.
 * @param useClimateAuthHeaders (optional) If true (default), some HTTP headers following TCC service conventions will
 *            be added. Note it is sometimes useful to set this to false to avoid use of CORS in a fetch to some
 *            service that isn't handling CORS properly.
 * @param userId (optional) Passed in x-user-id header as part of standard TCC service authorization.
 * @return Promise that resolves to a response.
 * @throws  The following kinds of errors will throw an Error exception:
 *            HTTP 401 when not in app mode
 *            HTTP 503 after exhausting reattempts
 *            Any other HTTP status code other than 404
 *            Network-level and CORS request errors after exhausting reattempts
 */
export const fetchJson = ({
  attemptCount = 1,
  extraHeaders,
  body,
  defaultResponse = {},
  isPagedData = false,
  isPaginatedImagery = false,
  method,
  modifyResponse,
  nextToken,
  redirect = 'follow',
  redirectsToLoginOnAuthFail = true,
  requiresOldApiDomain = false,
  useSameOrigin = false,
  timeout = timeouts.SERVICE_CALL,
  url,
  useClimateAuthHeaders = true,
  userId
}) => { // NOSONAR Excluding because fetch operation may be deprecated with DFP/OL migration soon TODO: Review after EMEA release 
  const toTryAgain = {
    attemptCount,
    body,
    defaultResponse,
    method,
    nextToken,
    redirectsToLoginOnAuthFail,
    requiresOldApiDomain,
    timeout,
    url,
    useClimateAuthHeaders,
    userId
  };

  if(window && window[FETCH_KILL_SWITCH]) {
    // Exists for debugging in the face of infinite loops only
    return Promise.reject('Killed by override');
  }

  if(isPagedData) {
    // return an array of response objects instead of a single response object
    return accumulatePages({ toTryAgain, fetchJson });
  }

  if(isPaginatedImagery) {
    return accumulateImageryPages({ toTryAgain, fetchJson });
  }

  const apiPrefix = determineApiPrefix({ requiresOldApiDomain });
  const headerOptions = generateHeaderOptions({ nextToken, useClimateAuthHeaders, userId, extraHeaders });
  let fetchOptions = generateFetchOptions({headerOptions, method, body, redirect, useSameOrigin, timeout});

  const fetchUrl = url.includes('//') ? url : `${apiPrefix}${url}`;
  const messageSource = stripQuerystring(fetchUrl);
  return Promise.race([

    rejectAfter(timeout/1000, `enforced timeout on ${url}`),

    fetch(fetchUrl, fetchOptions)
    // Only network or CORS errors are rejected promises.  HTTP errors are still resolved Promises.
    .catch(error => {
      error.message = `Failed to fetch: ${url}`;
      trackError(error, { url: fetchUrl });
      makeErrorHandler({ toTryAgain, attemptCount, messageSource, fetchJson });
    })
    .then((responseData) => { // NOSONAR False-Positive, complexity 11 of 10 due to additional graphQL error condition
      const response = responseData;
      const responseJson = responseData.json()
      .then(jsonData => modifyResponse ? modifyResponse(jsonData) : jsonData)
      .catch(error => {
        error.message = `Failed to fetch: ${url} Status Code: ${response.status}`;
        trackError(error, { url: fetchUrl,  statusCode: response.status });
      });


      if(!response) {
        throw new Error(`error: no response for ${messageSource}`);
      }

      if(response.status === HTTP.NOT_MODIFIED) {
        // 304 on paged data means the last page has no records
        return finalPageHandler({ response });
      }

      if(response.status === HTTP.PARTIAL_CONTENT) {
        return nextPageHandler({ response, responseJson });
      }

      if(response.ok) {
        if(attemptCount > 1) {
          return response; // If we're trying again, pass the raw response up the call chain to the first one
        }

        // Return the response data, performing a transformation first if a test case applies to this request.
        const responsePromise = responseJson;
        return _transformResponseIfApplicableTestCase({url, body, responsePromise});
      }

      if (response.status === HTTP.NOT_FOUND) {
        return defaultResponse; // 404s are often expected and not errors :(
      }

      if(response.status === HTTP.UNAUTHORIZED && redirectsToLoginOnAuthFail) {
        // Note: Many services say 401 instead of 403 or 400, so we should be selective about bouncing to login
        return authFailHandler({ toTryAgain, attemptCount, messageSource, fetchJson, response, defaultResponse });
      }

      if(response.status === HTTP.SERVICE_UNAVAILABLE ) {
        return serviceDownHandler({ toTryAgain, attemptCount, messageSource, fetchJson, response });
      }

      if(!response.ok) {
        trackError(response, { url: fetchUrl, statusCode: response.status });
        throw response;
      }

      return defaultResponse;
    })
  ]);
};